/**
 * 硬體 U 位佔用計算（ticket 04）。
 *
 * 需求書 DCK-05-02-01:選好機櫃與起始 U 位就要當場知道位置能不能放,
 * 不是等送出、更不是等到現場才發現兩台機器疊在同一格。
 *
 * 佔用區間定義為 [起始U, 起始U + U數 - 1]，只跟同機櫃的硬體比對；
 * 編輯既有硬體時排除自身，否則永遠跟自己衝突。
 */

/** 佔用區間;U 數沒填(還沒選型號)時當成 1U */
export const uRange = (uPosition, uSize) => {
  const start = Number(uPosition);
  const size = Number(uSize) || 1;
  return [start, start + size - 1];
};

const overlaps = ([aStart, aEnd], [bStart, bEnd]) => aStart <= bEnd && bStart <= aEnd;

/**
 * 這台硬體佔不佔位（兩段式新增 ticket 03）。
 *
 *   draft        還在審核中 → **不佔位也不保留**。位置要到核准才留給你，
 *                否則送一張單就能卡位，誰都不用等審核。
 *   reserved     已核准待上架 → 保留（位置留著，但機器還沒進機房）
 *   commissioned 已正式上架 → 正式佔用
 *
 * 沒有 `lifecycle` 的舊資料一律當成已上架 —— 既有資料本來就是在跑的設備。
 */
export const occupancyOf = (h) => {
  if (h.lifecycle === "draft") return null;
  return h.lifecycle === "reserved" ? "reserved" : "occupied";
};

/**
 * 機櫃「已用U」（ticket 28）—— 由該機櫃上架硬體實際佔用的 U 數加總推導,
 * 不再是機櫃自己填的欄位,避免跟 U 位防呆算出的結果不一致。
 * U 數規則與 uRange 一致:沒填(還沒選型號)時當成 1U。
 */
export function usedUOf(cabinetId, hardware = []) {
  const { occupied, reserved } = uUsageOf(cabinetId, hardware);
  return occupied + reserved;
}

/**
 * 已用U 拆成兩個數字（兩段式新增 ticket 05）。
 *
 * 「10U 已用」對機房人員來說不夠 —— 其中幾 U 是真的有機器在跑、幾 U 是留給
 * 還沒送到的機器，看到的下一步不一樣（後者是去催貨，不是想辦法騰位置）。
 * 草稿(審核中)兩邊都不算：它還沒拿到位置。
 */
export function uUsageOf(cabinetId, hardware = []) {
  return hardware
    .filter((h) => String(h.cabinetId) === String(cabinetId))
    .reduce((acc, h) => {
      const kind = occupancyOf(h);
      if (!kind) return acc;
      return { ...acc, [kind]: acc[kind] + (Number(h.uSize) || 1) };
    }, { occupied: 0, reserved: 0 });
}

/**
 * 機櫃的每一格 U（由上而下，第 uTotal 格在最上面 —— 機櫃實體就是這樣看的）。
 *
 * 回傳 `[{ u, state, hardware }]`，state 是 "free" / "occupied" / "reserved"。
 * 總U數缺值時回空陣列 —— 不知道有幾格就別假裝畫得出來。
 */
export function uSlotMap(cabinet, hardware = []) {
  const total = Number(cabinet && cabinet.uTotal);
  if (!(total > 0)) return [];
  const inCabinet = hardware.filter(
    (h) => String(h.cabinetId) === String(cabinet.id) && occupancyOf(h)
  );
  const slots = [];
  for (let u = total; u >= 1; u -= 1) {
    const hit = inCabinet.find((h) => {
      const [start, end] = uRange(h.uPosition, h.uSize);
      return u >= start && u <= end;
    });
    slots.push({ u, state: hit ? occupancyOf(hit) : "free", hardware: hit || null });
  }
  return slots;
}

/**
 * 找出這個位置的衝突。
 *
 * @returns null（沒問題）
 *        | { kind: "under", cabinet, range }    起始U 不是 ≥ 1 的整數
 *        | { kind: "over", cabinet, range }     尾端超出機櫃總U數
 *        | { kind: "overlap", cabinet, range, occupant, occupantRange, occupancy }
 *          跟別台重疊;`occupancy` 是 "occupied"(正式佔用)或 "reserved"(已核准待上架保留)
 */
export function findUConflict({ cabinet, hardware = [], uPosition, uSize, excludeId }) {
  // 機櫃或位置還沒填 → 還不到判定的時候,別急著噴錯
  if (!cabinet || uPosition === "" || uPosition === null || uPosition === undefined) return null;
  const range = uRange(uPosition, uSize);
  const [start, end] = range;
  if (!Number.isFinite(start)) return null;

  // 起始U 必須是 ≥ 1 的整數(0.5U、-1U 都不是真實的上架位置)
  if (!Number.isInteger(start) || start < 1) return { kind: "under", cabinet, range };
  // uTotal 缺值時無從判斷上界,不假裝檢查過(資料問題另案處理)
  const uTotal = Number(cabinet.uTotal);
  if (uTotal > 0 && end > uTotal) return { kind: "over", cabinet, range };

  const occupant = hardware.find((h) => {
    if (String(h.cabinetId) !== String(cabinet.id)) return false;
    if (excludeId != null && String(h.id) === String(excludeId)) return false;
    if (!occupancyOf(h)) return false; // 審核中的草稿不佔位
    return overlaps(range, uRange(h.uPosition, h.uSize));
  });
  if (!occupant) return null;

  return {
    kind: "overlap",
    cabinet,
    range,
    occupant,
    occupantRange: uRange(occupant.uPosition, occupant.uSize),
    occupancy: occupancyOf(occupant),
  };
}

/** 區間顯示:單格就一個數字,多格用 en dash */
const rangeText = ([start, end]) => (start === end ? `${start}U` : `${start}–${end}U`);

/** 訊息要說清楚是誰佔用了哪一段,使用者才知道該改位置還是先下架對方 */
export function uConflictMessage(conflict) {
  if (!conflict) return "";
  const { kind, cabinet, range } = conflict;
  if (kind === "under") return "起始 U 需為 1 以上的整數。";
  if (kind === "over") {
    return `超出機櫃 ${cabinet.code} 的 ${cabinet.uTotal}U 範圍（需要 ${rangeText(range)}）。`;
  }
  const { occupant, occupantRange, occupancy } = conflict;
  // 保留位的對面沒有機器 —— 講設備名稱會害人跑去機房找一台根本不存在的機器,
  // 要講的是那張單據,對方才知道該去找誰協調(兩段式新增 ticket 03)
  if (occupancy === "reserved") {
    const doc = occupant.docNo ? `單據 ${occupant.docNo}` : "一張已核准的單據";
    return `${rangeText(range)} 已被${doc}保留（${rangeText(occupantRange)}），`
      + "設備尚未上架。請與該單據的發動者協調。";
  }
  return `${rangeText(range)} 已被「${occupant.name || occupant.code}（${occupant.code}）」`
    + `佔用（${rangeText(occupantRange)}）。`;
}
